import { DateTime } from "luxon";
import { z } from "zod/v3";
import { PlainMessage, Timestamp } from "@bufbuild/protobuf";
import { ElementOf } from "@shared/common/types/typeHelpers";
import { createSchema } from "../zod-utils";
import { Actions_Type, RetShift as ProtoRetShift } from "@opstel/gen/knop/ui/v1/api/retshifts_pb";

export interface RetShift {
  id: string;
  serviceNumber: string;
  start: DateTime;
  end: DateTime;
  actions: RetShiftAction[];
}

export interface RetShiftAction {
  description: string;
  start: DateTime;
  end: DateTime;
  track: string;
  fromTrack: string;
  trainNumber: string;
  trainNumberDeparture: string;
  type?: RetShiftActionType;
}

export const RET_SHIFT_ACTION_TYPE = [
  "UNKNOWN",
  "MOVE",
  "VGM",
  "GVGM",
  "VGMS",
  "WALKING",
  "SCHAFT",
  "COMBINE",
  "SPLIT",
  "VOORTIJD",
  "NATIJD",
] as const;

export type RetShiftActionType = ElementOf<typeof RET_SHIFT_ACTION_TYPE>;

function transformRetShiftActionTypeEnum(actionsType: Actions_Type): RetShiftActionType {
  return RET_SHIFT_ACTION_TYPE[actionsType];
}

export const retShiftSchema = createSchema<RetShift, PlainMessage<ProtoRetShift>>({
  id: z.string(),
  serviceNumber: z.string(),
  start: z
    .instanceof(Timestamp, { message: "startTime should not be empty" })
    .refine((timestamp) => timestamp instanceof Timestamp, { message: "Invalid Timestamp" })
    .transform((val) => DateTime.fromJSDate(val.toDate())),
  end: z
    .instanceof(Timestamp, { message: "endTime should not be empty" })
    .refine((timestamp) => timestamp instanceof Timestamp, { message: "Invalid Timestamp" })
    .transform((val) => DateTime.fromJSDate(val.toDate())),
  actions: z.array(
    z.object({
      description: z.string(),
      start: z
        .instanceof(Timestamp)
        .refine((timestamp) => timestamp instanceof Timestamp, { message: "Invalid Timestamp" })
        .transform((val) => DateTime.fromJSDate(val.toDate())),
      end: z
        .instanceof(Timestamp)
        .refine((timestamp) => timestamp instanceof Timestamp, { message: "Invalid Timestamp" })
        .transform((val) => DateTime.fromJSDate(val.toDate())),
      track: z.string(),
      fromTrack: z.string(),
      trainNumber: z.string(),
      type: z.nativeEnum(Actions_Type).transform(transformRetShiftActionTypeEnum),
      trainNumberDeparture: z.string(),
    }),
  ),
});
